import React from 'react'
import { LucideIcon } from 'lucide-react'

interface PerformanceMetricCardProps {
  title: string
  value: string | number
  change?: string
  compareValue?: string | number
  compareChange?: string
  icon: LucideIcon
  color: string
  isActive: boolean
  onClick: () => void
}

export function PerformanceMetricCard({
  title,
  value,
  change,
  compareValue,
  compareChange,
  icon: Icon,
  color,
  isActive,
  onClick
}: PerformanceMetricCardProps) {
  return (
    <button
      onClick={onClick}
      className={`
        relative overflow-hidden
        bg-brand-dark-light rounded-monumenta p-6
        border-2 transition-all duration-300
        hover:shadow-monumenta-brand
        cursor-pointer text-left w-full
        ${isActive
          ? `border-${color} bg-${color}/5`
          : 'border-brand-gray/10 hover:border-brand-gray/30'
        }
      `}
    >
      {/* Active Indicator */}
      {isActive && (
        <div
          className={`absolute top-0 left-0 right-0 h-1 bg-${color}`}
        />
      )}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-display font-medium text-brand-gray uppercase tracking-wider">
          {title}
        </h3>
        <div className={`
          p-2 rounded-monumenta transition-all duration-200
          ${isActive ? `bg-${color}/20` : 'bg-brand-gray/10'}
        `}>
          <Icon
            className={`transition-all duration-200 ${isActive ? `text-${color}` : 'text-brand-gray'}`}
            size={20}
          />
        </div>
      </div>

      <div className="space-y-3">
        {/* Current Period */}
        <div>
          <div className={`
            text-3xl font-display font-bold transition-all duration-200
            ${isActive ? `text-${color}` : 'text-white'}
          `}>
            {value}
          </div>
          {change && (
            <div className={`
              text-sm font-medium transition-all duration-200 mt-1
              ${parseFloat(change) >= 0 ? 'text-success' : 'text-error'}
            `}>
              {change}
            </div>
          )}
        </div>

        {/* Comparison Period (if compare mode is on) */}
        {compareValue && (
          <div className="pt-3 border-t border-brand-gray/20">
            <div className="flex items-center justify-between">
              <span className="text-xs text-brand-gray uppercase tracking-wider">Previous</span>
              <div className="text-right">
                <div className="text-lg font-display font-semibold text-brand-gray">
                  {compareValue}
                </div>
                {compareChange && (
                  <div className="text-xs text-brand-gray/70 mt-0.5">
                    {compareChange}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </button>
  )
}
